import { useState } from "react";
import { UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WriteGuard } from "@/components/billing/write-guard";
import { useJoinRequestActions, useJoinRequests } from "@/hooks/queries/use-organization";
import type { JoinRequest } from "@/lib/api/organization";

function RequestRow({ request }: { request: JoinRequest }) {
  const { approve, decline } = useJoinRequestActions();
  const [error, setError] = useState<string | null>(null);
  const busy = approve.isPending || decline.isPending;
  const onError = (err: unknown) => {
    setError(err instanceof Error ? err.message : "Something went wrong.");
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="font-medium">{request.user.name}</div>
        <div className="truncate text-sm text-muted-foreground">{request.user.email}</div>
        {error && (
          <div className="mt-1 text-sm text-destructive" role="alert">
            {error}
          </div>
        )}
      </div>
      <WriteGuard>
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              setError(null);
              approve.mutate(request.id, { onError });
            }}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setError(null);
              decline.mutate(request.id, { onError });
            }}
          >
            Decline
          </Button>
        </div>
      </WriteGuard>
    </li>
  );
}

/**
 * Pending requests to join from people whose company email domain this
 * organization holds. Approved people join as members with no team access;
 * add them to teams afterwards. Renders nothing when there are none.
 */
export function JoinRequests() {
  const { data: requests } = useJoinRequests();
  if (!requests || requests.length === 0) return null;
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4">
      <h2 className="mb-1 flex items-center gap-2 text-base font-medium">
        <UserCheck className="h-4 w-4 text-primary" aria-hidden />
        Requests to join ({requests.length})
      </h2>
      <p className="mb-2 text-sm text-muted-foreground">
        Approved people join as members with no team access. Add them to a team afterwards.
      </p>
      <ul className="divide-y divide-border">
        {requests.map((r) => (
          <RequestRow key={r.id} request={r} />
        ))}
      </ul>
    </div>
  );
}
